// src/contexts/PWAInstallContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react"

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>
    usuarioResponse: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>
}

interface PWAInstallContextType {
    promptInstalacion: () => Promise<void> | null
    showInstallButton: boolean
    appInstalada: boolean
    isIOS: boolean
}

const PWAInstallContext = createContext<PWAInstallContextType>({
    promptInstalacion: () => null,
    showInstallButton: false,
    appInstalada: false,
    isIOS: false
})

export const usePWAInstall = () => useContext(PWAInstallContext)

export const InstalarPWAContext: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [capturedPrompt, setCapturedPrompt] = useState<BeforeInstallPromptEvent | null>(null)
    const [showInstallButton, setShowInstallButton] = useState(false)
    const [appInstalada, setAppInstalada] = useState(false)
    const [isIOS, setIsIOS] = useState(false)

    useEffect(() => {
        const checkInstallStatus = () => {
            const displayStandalone =
                window.matchMedia &&
                window.matchMedia("(display-mode: standalone)").matches
            const iosStandalone =
                (window.navigator as any).standalone === true
            setAppInstalada(displayStandalone || iosStandalone)
        }

        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault()
            setCapturedPrompt(e as BeforeInstallPromptEvent)
            setShowInstallButton(true)
        }

        const handleAppInstalled = () => {
            setAppInstalada(true)
            setShowInstallButton(false)
        }

        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
        window.addEventListener("appinstalled", handleAppInstalled)
        checkInstallStatus()

        setIsIOS(/iphone|ipad|ipod/i.test(window.navigator.userAgent))

        return () => {
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
            window.removeEventListener("appinstalled", handleAppInstalled)
        }
    }, [])

    const promptInstalacion = async () => {
        if (!capturedPrompt) return
        await capturedPrompt.prompt()
        setCapturedPrompt(null)
        setShowInstallButton(false)
    }


    return (
        <PWAInstallContext.Provider
            value={{ promptInstalacion, showInstallButton, appInstalada, isIOS }}
        >
            {children}
        </PWAInstallContext.Provider>
    )
}
